import { useEffect } from 'react';

/**
 * PSTUDIO-1433 (crash fix, second entrance): close an open row/cell context
 * menu as soon as the table scrolls.
 *
 * The menu's `ps-dropdown` moves ITSELF into the popover portal while open, so
 * React's `removeChild` runs against the wrong parent if that row is unmounted
 * while the menu is still up — `NotFoundError`, and the whole page drops to the
 * error boundary. The delayed-close guard fixes the gesture path (reopen while a
 * close is pending); this closes the other one: the table is virtualized, so
 * scrolling far enough unmounts the row out from under its own open menu, with
 * no user gesture involved at all.
 *
 * Scrolling away from a menu is also when a menu should close anyway, so the
 * behaviour costs nothing. The listener exists ONLY while a menu is open — the
 * table's scroll path is performance-critical and must stay untouched the rest
 * of the time — and it is passive + capture so it sees the virtualized
 * scroller's own scroll, which does not bubble to the document.
 */
export function useCloseMenuOnScroll(isOpen: boolean, close: () => void) {
  useEffect(() => {
    if (!isOpen) return undefined;
    const onScroll = () => close();
    document.addEventListener('scroll', onScroll, {
      capture: true,
      passive: true,
    });
    return () =>
      document.removeEventListener('scroll', onScroll, { capture: true });
  }, [isOpen, close]);
}
